import { staticAssetsApi } from "../../api/staticAssets";
import { PublicAdministrationStatus } from "../../features/personal-info/publicAdministration/publicAdministrationStore";
import { AttachmentType } from "../../features/personal-info/attachments/attachmentsStore";

import { IdentityConfirmationState } from "../../features/personal-info/tins/tinsStore";
import {
  CountryFormConfiguration,
  DEFAULT_NAMES_PATTERN,
  Gender,
} from "../constants";
import {
  DEFAULT_ADDRESS_LINE_PATTERN,
  defaultCountryConfigFieldLengths,
  defaultForbiddenWordsValidation,
  defaultShippingAddressCareOfNameValidation,
} from "./defaults";
import { UPLOAD_FILE_INVALID_EXTENSION } from "../../features/personal-info/attachments/validations/attachmentValidations";
import { DsaLogo } from "../../components/common/Logo/models/Logo";
import dayjs from "dayjs";
import { sanitizeStringValue } from "../../utils/replacement";
import { SanitizeMode } from "../next-gen-config-wip";
import { getAddressData } from "../../api/avs";

const FISC_TIN_REGEX = new RegExp(
  "^[a-zA-Z]{6}\\d{2}[a-zA-Z]{1}\\d{2}[a-zA-Z]{1}\\d{1}[a-zA-Z0-9]{1}\\d{1}[a-zA-Z]{1}$"
);

const UE_COUNTRIES = [
  "AUSTRIA",
  "BELGIO",
  "BULGARIA",
  "CROAZIA",
  "CIPRO",
  "REPUBBLICA CECA",
  "DANIMARCA",
  "ESTONIA",
  "FINLANDIA",
  "FRANCIA",
  "GERMANIA",
  "GRECIA",
  "UNGHERIA",
  "IRLANDA",
  "ITALIA",
  "LETTONIA",
  "LITUANIA",
  "LUSSEMBURGO",
  "MALTA",
  "PAESI BASSI",
  "POLONIA",
  "PORTOGALLO",
  "ROMANIA",
  "SLOVACCHIA",
  "SLOVENIA",
  "SPAGNA",
  "SVEZIA",
];

const placeOfBirthCountryGetOptions = async (): Promise<string[]> =>
  await staticAssetsApi.getStaticAssets<string[]>("it-it", "countries.json");

const getAddresses = async (areas: string[]) =>
  (await getAddressData("it-IT", areas)).values;

const getProvinces = async (): Promise<string[]> => await getAddresses([]);

const getCities = async (selectedProvince: string): Promise<string[]> =>
  await getAddresses([selectedProvince]);

export const Italy: CountryFormConfiguration = {
  id: "it-IT",
  forms: {
    personalInfo: {
      beenMemberBefore: {
        name: "beenMemberBefore",
        label: "PREVIOUS_MEMBERSHIP",
        labelExtraClassNames: ["enlargedLabelText"],
        type: "radio",
        options: [
          { value: true, label: "YES" },
          { value: false, label: "NO" },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
        classNames: ["centerJustified"],
        validationErrorStyling: ["textCentered"],
      },
      firstName: {
        name: "firstName",
        label: "FIRST_NAME",
        type: "text",
        sanitize: [
          { mode: SanitizeMode.ON_CHANGE, sanitizeFn: sanitizeStringValue },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "FIRST_NAME_SIZE",
          },
          maxLength: {
            value: defaultCountryConfigFieldLengths.firstName,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: DEFAULT_NAMES_PATTERN,
            errorMessage: "FIRST_NAME_SIZE",
          },
          trim: true,
        },
      },
      lastName: {
        name: "lastName",
        label: "LAST_NAME",
        type: "text",
        sanitize: [
          { mode: SanitizeMode.ON_CHANGE, sanitizeFn: sanitizeStringValue },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "LAST_NAME_SIZE",
          },
          maxLength: {
            value: defaultCountryConfigFieldLengths.lastName,
            errorMessage: "TOO_LONG",
          },
          pattern: {
            value: DEFAULT_NAMES_PATTERN,
            errorMessage: "LAST_NAME_SIZE",
          },
          trim: true,
        },
      },
      gender: {
        name: "gender",
        label: "GENDER",
        type: "radio",
        options: [
          { value: Gender.MALE, label: "MALE" },
          { value: Gender.FEMALE, label: "FEMALE" },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
        validationErrorStyling: ["textCentered"],
      },
      placeOfBirth: {
        name: "placeOfBirth",
        label: "PLACE_OF_BIRTH",
        type: "object",
        validation: {
          required: { value: false },
        },
        rule: {
          isHidden: true,
        },
        fields: {
          country: {
            name: "placeOfBirthCountry",
            label: "PLACE_OF_BIRTH_COUNTRY",
            type: "asyncAutocomplete",
            defaultValue: "ITALIA",
            getOptions: placeOfBirthCountryGetOptions,
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
              maxLength: { value: 40, errorMessage: "TOO_LONG" },
            },
          },
          province: {
            name: "placeOfBirthProvince",
            label: "PLACE_OF_BIRTH_PROVINCE",
            type: "asyncAutocomplete",
            defaultValue: "",
            dependenciesNames: ["placeOfBirthCountry"],
            getOptions: getProvinces,
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField: "placeOfBirthCountry",
                  visibleRuleFunction: (placeOfBirthCountry) =>
                    (placeOfBirthCountry as string) === "ITALIA",
                },
              ],
            },
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
            },
          },
          city: {
            name: "placeOfBirthCity",
            label: "PLACE_OF_BIRTH_CITY",
            type: "asyncAutocomplete",
            defaultValue: "",
            getOptions: getCities,
            dependenciesNames: ["placeOfBirthProvince"],
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField: "placeOfBirthProvince",
                  visibleRuleFunction: (placeOfBirthProvince) =>
                    Boolean(placeOfBirthProvince as string),
                },
              ],
            },
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
            },
          },
          residencePermitAttachment: {
            name: "placeOfBirth-residencePermit-attachment",
            label: "PLACE_OF_BIRTH_ATTACHMENT",
            key: "place-of-birth-attachment",
            type: "file",
            index: 0,
            attachmentType: AttachmentType.RESIDENCE_PERMIT,
            middleDescription: {
              text: "PLACE_OF_BIRTH_ATTACHMENT_DESCRIPTION",
            },
            validation: {
              acceptedExtensions: {
                value: [".pdf", ".bmp", ".jpg", ".jpeg"],
                errorMessage: "UPLOAD_FILE_INVALID_EXTENSION",
              },
              required: { value: false, errorMessage: "REQUIRED" },
            },
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField: "placeOfBirthCountry",
                  visibleRuleFunction: (placeOfBirthCountry) =>
                    !UE_COUNTRIES.includes(placeOfBirthCountry as string),
                },
              ],
            },
          },
        },
      },
      profilePhotoAttachment: {
        name: "profile-photo-attachment",
        label: "PROFILE_PHOTO_UPLOAD_FILE_INFO",
        key: "profile-photo-attachment",
        type: "file",
        placeholder: "UPLOAD_FACE_PICTURE_DISCLAIMER",
        index: 0,
        attachmentType: AttachmentType.FACE_PHOTO,
        topDescription: { text: "PROFILE_PHOTO_UPLOAD_FILE_LABEL" },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          acceptedExtensions: {
            value: [".bmp", ".jpg", ".jpeg"],
            errorMessage: "PROFILE_PHOTO_INVALID_EXTENSION",
          },
        },
      },
      isPublicAdministrationEmployee: {
        name: "isPublicAdministrationEmployee",
        label: "IS_PUBLIC_ADMINISTRATION",
        type: "radio",
        defaultValue: PublicAdministrationStatus.NOT_EMPLOYED,
        options: [
          {
            value: PublicAdministrationStatus.EMPLOYED,
            label: PublicAdministrationStatus.EMPLOYED,
          },
          {
            value: PublicAdministrationStatus.NOT_EMPLOYED,
            label: PublicAdministrationStatus.NOT_EMPLOYED,
          },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
        classNames: ["centerJustified"],
      },
      publicAdministrationAttachment: {
        name: "publicAdministration-attachment",
        label: "PUBLIC_ADMINISTRATION_ATTACHMENT",
        key: "publicAdministration-attachment",
        middleDescription: {
          text: "PUBLIC_ADMINISTRATION_ATTACHMENT_DISCLAIMER",
        },
        type: "file",
        index: 0,
        attachmentType: AttachmentType.AUTHORIZATION_DOCUMENT,
        validation: {
          required: { value: false, errorMessage: "REQUIRED" },
          acceptedExtensions: {
            value: [".pdf", ".bmp", ".jpg", ".jpeg"],
            errorMessage: "UPLOAD_FILE_INVALID_EXTENSION",
          },
        },
        rule: {
          visibleOn: [
            {
              visibilityDeterminingField: "isPublicAdministrationEmployee",
              visibleRuleFunction: (isPublicAdministrationEmployee) =>
                (isPublicAdministrationEmployee as string) ===
                PublicAdministrationStatus.EMPLOYED.toString(),
            },
          ],
        },
      },
      addressLine1: {
        name: "addressLine1",
        label: "ADDRESS",
        type: "experian",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 50,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          pattern: {
            value: DEFAULT_ADDRESS_LINE_PATTERN,
            errorMessage: "FORBIDDEN_WORD",
          },
          trim: true,
        },
      },
      postalCode: {
        name: "postalCode",
        label: "POSTAL_CODE",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: { value: 5, errorMessage: "TOO_LONG" },
          minLength: { value: 5, errorMessage: "POSTAL_CODE_SIZE" },
          pattern: {
            value: /^[0-9]{5}$/,
            errorMessage: "POSTAL_CODE_SIZE",
          },
          mask: "00000",
        },
      },
      province: {
        name: "province",
        label: "PROVINCE",
        type: "asyncAutocomplete",
        defaultValue: "",
        getOptions: getProvinces,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 40,
            errorMessage: "TOO_LONG",
          },
        },
      },
      city: {
        name: "city",
        label: "CITY",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: { value: 40, errorMessage: "TOO_LONG" },
          trim: true,
        },
      },
      country: {
        name: "country",
        label: "COUNTRY",
        type: "text",
        defaultValue: "Italy",
        validation: {
          required: { value: false },
        },
        rule: {
          isDisabled: true,
        },
      },
      dateOfBirth: {
        name: "dateOfBirth",
        label: "BIRTHDAY",
        type: "date",
        bottomDescription: { text: "BIRTHDAY_DISCLAIMER" },
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxDate: {
            value: dayjs().subtract(18, "year"),
            errorMessage: "TOO_YOUNG",
          },
          minDate: {
            value: dayjs().subtract(120, "year"),
            errorMessage: "TOO_OLD",
          },
        },
      },
      registrationNumber0: {
        name: "registrationNumber0",
        label: "REGISTRATION_NUMBER0",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 16,
            errorMessage: "REGISTRATION_NUMBER_SIZE",
          },
          minLength: {
            value: 16,
            errorMessage: "REGISTRATION_NUMBER_SIZE",
          },
          pattern: {
            value: FISC_TIN_REGEX,
            errorMessage: "REGISTRATION_NUMBER_INVALID_PATTERN",
          },
          identityConfirmation: true,
          trim: true,
        },
        rule: {
          visibleOn: [
            {
              visibilityDeterminingField: "$identityConfirmationState",
              visibleRuleFunction: (identityConfirmationState) =>
                (identityConfirmationState as IdentityConfirmationState) !==
                IdentityConfirmationState.FAILED,
            },
          ],
          keepValueOnHidden: true,
        },
      },
      registrationNumber0Attachment0: {
        name: "registrationNumber0-attachment0",
        label: "UPLOAD_FILE_INFO_RESIDENT0",
        type: "file",
        index: 1,
        attachmentType: AttachmentType.TAX_NUMBER,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          acceptedExtensions: {
            value: [".pdf", ".bmp", ".jpg", ".jpeg"],
            errorMessage: UPLOAD_FILE_INVALID_EXTENSION,
          },
        },
        rule: {
          visibleOn: [
            {
              visibilityDeterminingField: "$identityConfirmationState",
              visibleRuleFunction: (identityConfirmationState) =>
                (identityConfirmationState as IdentityConfirmationState) ===
                IdentityConfirmationState.FAILED,
            },
          ],
        },
      },
      dataPrivacy: {
        name: "dataPrivacy",
        header: "DATA_PRIVACY_HEADER",
        type: "static",
        staticValue: "DATA_PRIVACY_STATEMENT",
      },
      dataPrivacyDisclosure: {
        name: "dataPrivacyDisclosure",
        type: "static",
        staticValue: "INFORMATION_DISCLOSURE",
      },
      additionalComunicationConsent: {
        name: "additionalComunicationConsent",
        type: "static",
        staticValue: "ADDITIONAL_COMMUNICATION_CONSENT",
      },
      marketingConsentAccepted: {
        name: "marketingConsentAccepted",
        label: "EXTRA_COMMUNICATION_CONSENT",
        type: "checkbox",
        validation: {
          required: { value: false },
        },
      },
      dataPrivacyAcknowledgementAccepted: {
        name: "dataPrivacyAcknowledgementAccepted",
        label: "DATA_PRIVACY_ACKNOWLEDGEMENT",
        type: "checkbox",
        validation: {
          required: { value: false },
        },
      },
      prospectOwnsSignificantSocialMediaAccount: {
        name: "prospectOwnsSignificantSocialMediaAccount",
        label: "SOCIAL_MEDIA_FOLLOWERS_QUESTION",
        type: "radio",
        options: [
          { value: true, label: "YES" },
          { value: false, label: "NO" },
        ],
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
        },
        classNames: ["centerJustified"],
        validationErrorStyling: ["textCentered"],
      },
      socialMediaConsents: {
        name: "socialMediaConsentsWrapper",
        label: "SOCIAL_MEDIA",
        type: "object",
        classNames: ["collapse"],
        rule: {
          visibleOn: [
            {
              visibilityDeterminingField:
                "prospectOwnsSignificantSocialMediaAccount",
              visibleRuleFunction: (field) => Boolean(field),
            },
          ],
        },
        validation: {
          required: { value: false },
        },
        isCollapsable: true,
        fields: {
          customerAcknowledgementInfo: {
            name: "customerAcknowledgementInfo",
            type: "static",
            staticValue: "CUSTOMER_ACKNOWLEDGEMENT_INFO",
          },
          socialMediaConsent0: {
            name: "socialMediaConsent0",
            label: "CUSTOMER_SERVICE",
            type: "checkbox",
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
            },
            bottomDescription: { text: "CONTACT_CONSENT" },
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField:
                    "prospectOwnsSignificantSocialMediaAccount",
                  visibleRuleFunction: (field) => Boolean(field),
                },
              ],
            },
            defaultValue: false,
          },
          socialMediaConsent1: {
            name: "socialMediaConsent1",
            label: "CUSTOMER_LEAD",
            type: "checkbox",
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
            },
            bottomDescription: { text: "CUSTOMER_LEAD_CONSENT" },
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField:
                    "prospectOwnsSignificantSocialMediaAccount",
                  visibleRuleFunction: (field) => Boolean(field),
                },
              ],
            },
            defaultValue: false,
          },
          socialMediaConsent2: {
            name: "socialMediaConsent2",
            label: "SALES_AND_MARKETING_PLAN",
            type: "checkbox",
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
            },
            bottomDescription: { text: "SALES_AND_MARKETING_PLAN_CONSENT" },
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField:
                    "prospectOwnsSignificantSocialMediaAccount",
                  visibleRuleFunction: (field) => Boolean(field),
                },
              ],
            },
            defaultValue: false,
          },
          socialMediaConsent3: {
            name: "socialMediaConsent3",
            label: "NO_ASSOCIATION",
            type: "checkbox",
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
            },
            bottomDescription: { text: "NO_ASSOCIATION_CONSENT" },
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField:
                    "prospectOwnsSignificantSocialMediaAccount",
                  visibleRuleFunction: (field) => Boolean(field),
                },
              ],
            },
            defaultValue: false,
          },
          socialMediaConsent4: {
            name: "socialMediaConsent4",
            label: "SOCIAL_MEDIA",
            type: "checkbox",
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
            },
            bottomDescription: { text: "SOCIAL_MEDIA_CONSENT" },
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField:
                    "prospectOwnsSignificantSocialMediaAccount",
                  visibleRuleFunction: (field) => Boolean(field),
                },
              ],
            },
            defaultValue: false,
          },
          socialMediaConsent5: {
            name: "socialMediaConsent5",
            label: "EARNINGS_CLAIMS",
            type: "checkbox",
            validation: {
              required: { value: true, errorMessage: "REQUIRED" },
            },
            bottomDescription: { text: "EARNINGS_CLAIMS_CONSENT" },
            rule: {
              visibleOn: [
                {
                  visibilityDeterminingField:
                    "prospectOwnsSignificantSocialMediaAccount",
                  visibleRuleFunction: (field) => Boolean(field),
                },
              ],
            },
            defaultValue: false,
          },
          socialMediaEndingNote: {
            name: "socialMediaEndingNote",
            type: "static",
            staticValue: "SOCIAL_MEDIA_ENDING_NOTE",
          },
        },
      },
    },
    shippingAddress: {
      careOfName: {
        name: "careOfName",
        label: "HMP_CARE_OF_NAME",
        sanitize: [
          { mode: SanitizeMode.ON_CHANGE, sanitizeFn: sanitizeStringValue },
        ],
        type: "text",
        validation: defaultShippingAddressCareOfNameValidation,
      },
      addressLine1: {
        name: "addressLine1",
        label: "ADDRESS",
        type: "experian",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          hasNoOnlySpecialSigns: {
            value: true,
            errorMessage: "ADDRESS_SIZE",
          },
          maxLength: {
            value: 50,
            errorMessage: "ADDRESS_SIZE",
          },
          forbiddenWords: defaultForbiddenWordsValidation,
          pattern: {
            value: DEFAULT_ADDRESS_LINE_PATTERN,
            errorMessage: "FORBIDDEN_WORD",
          },
          trim: true,
        },
      },
      city: {
        name: "city",
        label: "CITY",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: { value: 40, errorMessage: "TOO_LONG" },
          trim: true,
        },
      },
      province: {
        name: "province",
        label: "PROVINCE",
        type: "asyncAutocomplete",
        defaultValue: "",
        getOptions: getProvinces,
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: {
            value: 40,
            errorMessage: "TOO_LONG",
          },
        },
      },
      postalCode: {
        name: "postalCode",
        label: "POSTAL_CODE",
        type: "text",
        validation: {
          required: { value: true, errorMessage: "REQUIRED" },
          maxLength: { value: 5, errorMessage: "TOO_LONG" },
          minLength: { value: 5, errorMessage: "POSTAL_CODE_SIZE" },
          pattern: {
            value: /^[0-9]{5}$/,
            errorMessage: "POSTAL_CODE_SIZE",
          },
          mask: "00000",
        },
      },
    },
  },
  addressLength: 50,
  addressPattern: DEFAULT_ADDRESS_LINE_PATTERN,
  cityLength: 40,
  postalCodeLength: 5,
  postalCodeMask: "00000",
  postalCodePattern: /^[0-9]{5}$/,
  postalCodeRequired: true,
  province: true,
  provinceLength: 40,
  provinceRequired: true,
  showDeliveryDisclaimer: false,
  defaultCountryValue: "Italia",
  firstNameLength: defaultCountryConfigFieldLengths.firstName,
  footerLinks: ["PRIVACY", "COOKIE_POLICY", "RULES_OF_CONDUCT", "COPYRIGHT"],
  footerLogos: [DsaLogo],
  deliveryPriceKeys: ["retail", "deliveryTotal", "tax", "totalAmountToPay"],
  genderFieldRequired: true,
  lastNameLength: defaultCountryConfigFieldLengths.lastName,
  lastNameFirst: false,
  middleInitial: false,
  middleInitialLength: defaultCountryConfigFieldLengths.middleInitial,
  middleInitialRequired: false,
  mothersMaidenName: false,
  mothersMaidenNameLength: defaultCountryConfigFieldLengths.mothersMaidenName,
  mothersMaidenNameRequired: false,
  showDataPrivacyAcknowledgment: true,
  showDocumentationBox: false,
  showHmpBenefits: true,
  showHmpPickUpSection: false,
  showHmpPriceContent: true,
  showGsgBenefits: true,
  showOnboardingDocumentsLink: true,
  showLatinInfo: false,
  showLinkToOrderConfirmation: true,
  showPdfContainer: true,
  showResidentRadioBox: false,
  showSocialMediaStatement: true,
  spouseFirstName: false,
  spouseFirstNameLength: defaultCountryConfigFieldLengths.spouseFirstName,
  spouseMiddleName: false,
  spouseMiddleNameLength: defaultCountryConfigFieldLengths.spouseMiddleName,
  spouseLastName: false,
  spouseLastNameLength: defaultCountryConfigFieldLengths.spouseLastName,
  showOrderHandlerInformation: false,
  showPaymentAcknowledgment: false,
  showPaymentDisclaimer: true,
  supportedCountryCodes: ["it"],
  defaultCountryCode: "+39|Italy",
  isDataPrivacyAcknowledgement: true,
  isBottomDataPrivacyAcknowledgement: true,
} as const;
